(() => {
  'use strict';

  if (window.__cpgCartBridge) return;

  const config = document.querySelector('[data-cpg-cart-config]');
  if (!config) return;

  window.__cpgCartBridge = true;

  const store = config.dataset.storeId || '';

  const t = {
    loading: 'Opening checkout…',
    failure: 'Checkout could not be opened. Please try again.',
    empty: 'Your cart is empty.',
    subscription: 'Subscriptions are not supported by this checkout.',
    timeout: 'The connection took too long. Please try again; your existing payment attempt will be reused.',
    changed: 'Your cart or the store settings have changed. Please reload the page and try again.',
    paid: 'This order has already been paid. Please check your order confirmation.'
  };

  function localError(key) {
    const error = new Error(t[key]);
    error.customerMessage = t[key];
    return error;
  }

  let endpoint;

  try {
    const base = new URL(config.dataset.hubUrl);

    if (
      base.protocol !== 'https:' ||
      base.username ||
      base.password
    ) {
      throw new Error();
    }

    base.search = '';
    base.hash = '';

    endpoint = new URL(
      'wp-json/cpg/v1/shopify/cart',
      base.href.replace(/\/?$/, '/')
    );
  } catch (_) {
    /* A matching checkout button displays the configuration error. */
  }

  const defaults =
    'button[name="checkout"],' +
    'input[name="checkout"],' +
    '#CartDrawer-Checkout,' +
    '#checkout,' +
    'button[data-cpg-checkout],' +
    'a[data-cpg-checkout],' +
    '[role="button"][data-cpg-checkout],' +
    'a[href="/checkout"],' +
    'a[href^="/checkout?"]';

  let selector = defaults;

  if (config.dataset.checkoutSelector) {
    try {
      document.querySelector(config.dataset.checkoutSelector);
      selector += ',' + config.dataset.checkoutSelector;
    } catch (_) {
      /* Built-in selectors remain usable with an invalid custom one. */
    }
  }

  let busy = false;
  let active = null;
  let loadingStyle = false;

  function prepareLoadingStyle() {
    if (!loadingStyle) {
      const style = document.createElement('style');

      style.textContent = `
        [data-cpg-button-loading] {
          cursor: wait !important;
        }

        [data-cpg-button-loading] .cpg-button-progress {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          pointer-events: none;
        }

        [data-cpg-button-loading] .cpg-loading-spinner {
          display: inline-block;
          flex: none;
          width: 16px;
          height: 16px;
          box-sizing: border-box;
          border: 2px solid currentColor;
          border-right-color: transparent;
          border-radius: 50%;
          animation: cpg-loading-spin .8s linear infinite;
        }

        @keyframes cpg-loading-spin {
          to {
            transform: rotate(360deg);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          [data-cpg-button-loading] .cpg-loading-spinner {
            animation: none;
          }
        }
      `;

      document.head.appendChild(style);
      loadingStyle = true;
    }
  }

  const memory = new Map();

  const read = key => {
    try {
      return sessionStorage.getItem(key) || memory.get(key);
    } catch (_) {
      return memory.get(key);
    }
  };

  const save = (key, value) => {
    memory.set(key, value);

    try {
      sessionStorage.setItem(key, value);
    } catch (_) {}
  };

  const forget = key => {
    memory.delete(key);

    try {
      sessionStorage.removeItem(key);
    } catch (_) {}
  };

  function control(target) {
    if (!(target instanceof Element)) return null;

    const button = target.closest(selector);

    if (!button || button.closest('[data-cpg-ignore]')) {
      return null;
    }

    return button;
  }

  function setBusy(button, state) {
    busy = state;

    if (state) {
      prepareLoadingStyle();

      active = {
        button,
        disabled: button.disabled,
        html: button.innerHTML,
        value: button.value
      };

      button.setAttribute('aria-busy', 'true');
      button.setAttribute('data-cpg-button-loading', 'true');

      if ('disabled' in button) {
        button.disabled = true;
      }

      if (button.tagName === 'INPUT') {
        button.value = t.loading;
      } else {
        button.innerHTML =
          '<span class="cpg-button-progress">' +
            '<span class="cpg-loading-spinner" aria-hidden="true"></span>' +
            '<span role="status">' + t.loading + '</span>' +
          '</span>';
      }
    } else if (active) {
      const old = active;

      old.button.removeAttribute('aria-busy');
      old.button.removeAttribute('data-cpg-button-loading');

      if ('disabled' in old.button) {
        old.button.disabled = old.disabled;
      }

      if (old.button.tagName === 'INPUT') {
        old.button.value = old.value;
      } else {
        old.button.innerHTML = old.html;
      }

      active = null;
    }
  }

  function message(button, text) {
    let box = document.querySelector('[data-cpg-cart-error]');

    if (!box) {
      box = document.createElement('p');
      box.dataset.cpgCartError = '';
      box.setAttribute('role', 'alert');

      button.insertAdjacentElement('afterend', box);
    }

    box.textContent = text;
    box.hidden = false;
  }

  async function json(url, options) {
    const controller = new AbortController();

    const timeout = setTimeout(
      () => controller.abort(),
      65000
    );

    try {
      const response = await fetch(url, {
        cache: 'no-store',
        ...options,
        signal: controller.signal
      });

      const result = await response.json();

      if (!response.ok) {
        const error = localError(
          result.code === 'already_paid'
            ? 'paid'
            : [
                'cart_total',
                'cart_currency',
                'mode_changed',
                'checkout_restart',
                'shop_changed'
              ].includes(result.code)
              ? 'changed'
              : 'failure'
        );

        error.code = result.code;

        throw error;
      }

      return result;
    } finally {
      clearTimeout(timeout);
    }
  }

  function localCartUrl() {
    const root =
      window.Shopify?.routes?.root ||
      config.dataset.cartRoot ||
      '/';

    const url = new URL(
      root.replace(/\/?$/, '/') + 'cart.js',
      location.origin
    );

    if (url.origin !== location.origin) {
      throw localError('failure');
    }

    return url;
  }

  async function start(button) {
    if (
      busy ||
      button.disabled ||
      button.getAttribute('aria-disabled') === 'true'
    ) {
      return;
    }

    setBusy(button, true);

    const previousError =
      document.querySelector('[data-cpg-cart-error]');

    if (previousError) {
      previousError.hidden = true;
    }

    let key;

    try {
      if (!endpoint || !store) {
        throw localError('failure');
      }

      // Fetch immediately before checkout so dynamically rendered drawers
      // and cart apps use the same current Shopify cart.
      // No DOM prices are trusted.
      const cart = await json(
        localCartUrl(),
        {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json'
          }
        }
      );

      if (!cart.items?.length) {
        throw localError('empty');
      }

      if (
        cart.items.some(
          item => item.selling_plan_allocation
        )
      ) {
        throw localError('subscription');
      }

      const discounts = [
        ...(cart.discount_codes || [])
          .filter(d => d.applicable !== false)
          .map(d => d.code),

        ...(cart.cart_level_discount_applications || [])
          .filter(d => d.type === 'discount_code')
          .map(d => d.title),

        ...cart.items.flatMap(i =>
          (i.line_level_discount_allocations || [])
            .filter(
              d =>
                d.discount_application?.type ===
                'discount_code'
            )
            .map(
              d => d.discount_application.title
            )
        )
      ];

      const body = {
        store_id: store,
        currency: cart.currency,
        cart_total: cart.total_price,

        country:
          window.Shopify?.country ||
          config.dataset.country ||
          '',

        discount_codes: [
          ...new Set(
            discounts.filter(Boolean)
          )
        ],

        items: cart.items.map(i => ({
          variant_id: String(i.variant_id),
          quantity: i.quantity,
          properties: i.properties || {}
        }))
      };

      const digest = await crypto.subtle.digest(
        'SHA-256',
        new TextEncoder().encode(
          JSON.stringify({
            token: cart.token,
            ...body
          })
        )
      );

      const hash = Array.from(
        new Uint8Array(digest),
        b =>
          b
            .toString(16)
            .padStart(2, '0')
      ).join('');

      key =
        `cpg:cart:100:${endpoint.origin}:${store}:${hash}`;

      let requestId = read(key);

      if (!requestId) {
        requestId = crypto.randomUUID();
        save(key, requestId);
      }

      const result = await json(
        endpoint,
        {
          method: 'POST',
          credentials: 'omit',

          headers: {
            'Content-Type': 'application/json'
          },

          body: JSON.stringify({
            ...body,
            request_id: requestId
          })
        }
      );

      const url = new URL(
        result.checkout_url
      );

      if (
        url.protocol !== 'https:' ||
        ![
          endpoint.origin,
          'https://checkout.stripe.com'
        ].includes(url.origin) ||
        url.username ||
        url.password
      ) {
        throw localError('failure');
      }

      location.assign(url.href);

    } catch (error) {
      // Only a server-confirmed closed unpaid attempt
      // may get a fresh ID.
      // Keep the ID after uncertain failures
      // and already-paid responses.
      if (
        [
          'mode_changed',
          'checkout_restart'
        ].includes(error.code) &&
        key
      ) {
        forget(key);
      }

      message(
        button,
        error.name === 'AbortError'
          ? t.timeout
          : error.customerMessage || t.failure
      );

      setBusy(button, false);
    }
  }

  // Delegated listeners keep working when
  // a drawer app replaces its buttons.
  document.addEventListener(
    'click',
    event => {
      const button = control(event.target);

      if (!button) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      start(button);
    },
    true
  );

  document.addEventListener(
    'submit',
    event => {
      let button = control(event.submitter);

      if (
        !button &&
        !event.submitter &&
        event.target instanceof Element
      ) {
        button =
          event.target.querySelector(selector);
      }

      if (!button) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      start(button);
    },
    true
  );

  window.addEventListener(
    'pageshow',
    () => {
      if (active) {
        setBusy(active.button, false);
      }
    }
  );
})();